import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { MdHistory, MdOutlineStar } from "react-icons/md";
import { SEARCH_EVENT } from "./navPrefs";
import { searchItems } from "./navModules";
import "./navV2.css";

/**
 * "Jump to" search over every page this person can open (Ctrl+K / Cmd+K, or the
 * navbar search button). Pages only - nothing here grants access the menu doesn't.
 */
export default function CommandPalette({ items, byKey, pins, recent }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const returnFocus = useRef(null);
  const navigate = useNavigate();

  const show = useCallback(() => {
    returnFocus.current = document.activeElement;
    setQuery("");
    setSel(0);
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    setOpen(false);
    const el = returnFocus.current;
    if (el && typeof el.focus === "function") el.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => {
          if (!o) {
            returnFocus.current = document.activeElement;
            setQuery("");
            setSel(0);
          }
          return !o;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(SEARCH_EVENT, show);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(SEARCH_EVENT, show);
    };
  }, [show]);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  const groups = useMemo(() => {
    if (query.trim()) return [{ title: "Pages", items: searchItems(items, query) }];
    const pick = (keys) => keys.map((k) => byKey.get(k)).filter(Boolean);
    const pinned = pick(pins);
    const recents = pick(recent).filter((i) => !pins.includes(i.key));
    return [
      { title: "Pinned", items: pinned, icon: MdOutlineStar },
      { title: "Recent", items: recents, icon: MdHistory },
    ].filter((g) => g.items.length);
  }, [query, items, byKey, pins, recent]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const go = (item) => {
    if (!item) return;
    setOpen(false);
    navigate(item.to);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[sel]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      hide();
    }
  };

  // Keep the highlighted row in view while arrowing.
  useEffect(() => {
    if (!open) return;
    document.getElementById(`nv2-pal-opt-${sel}`)?.scrollIntoView?.({ block: "nearest" });
  }, [sel, open]);

  if (!open) return null;

  let index = -1;
  return ReactDOM.createPortal(
    <div className="nv2-pal-scrim" onMouseDown={(e) => e.target === e.currentTarget && hide()}>
      <div className="nv2-pal" role="dialog" aria-modal="true" aria-label="Jump to a page">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSel(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Jump to a page…  e.g. repair, journal, leave"
          aria-label="Search pages"
          role="combobox"
          aria-expanded="true"
          aria-controls="nv2-pal-list"
          aria-activedescendant={flat.length ? `nv2-pal-opt-${sel}` : undefined}
          autoComplete="off"
        />
        <ul className="nv2-pal-list" id="nv2-pal-list" role="listbox">
          {groups.map((g) => (
            <li key={g.title} role="presentation">
              <div className="nv2-pal-group">{g.title}</div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }} role="presentation">
                {g.items.map((item) => {
                  index += 1;
                  const i = index;
                  const Icon = g.icon || item.icon;
                  return (
                    <li
                      key={`${g.title}-${item.key}`}
                      id={`nv2-pal-opt-${i}`}
                      role="option"
                      aria-selected={i === sel}
                      className={`nv2-pal-item${i === sel ? " is-sel" : ""}`}
                      onMouseEnter={() => setSel(i)}
                      onClick={() => go(item)}
                    >
                      {Icon ? <Icon size={18} /> : null}
                      <span>{item.label}</span>
                      <small>
                        {item.moduleLabel}
                        {item.section ? ` › ${item.section}` : ""}
                      </small>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
          {!flat.length && (
            <li className="nv2-pal-empty" role="presentation">
              {query.trim() ? "No page matches that." : "Type to search every page you can open. Pinned and recent pages show here."}
            </li>
          )}
        </ul>
        <div className="nv2-pal-foot">
          <span>↑↓ move</span>
          <span>Enter open</span>
          <span>Esc close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
